import { useMemo, useState } from "react";
import type { Bank } from "../api";
import BankLogo from "./BankLogo";
import StatusMessage from "./StatusMessage";

const POPULAR_SHORT_NAMES = [
  "Vietcombank",
  "VietinBank",
  "BIDV",
  "Agribank",
  "MBBank",
  "Techcombank",
  "ACB",
  "VPBank",
  "TPBank",
  "Sacombank",
  "VIB",
  "HDBank",
];

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function pickPopularBanks(banks: Bank[]): Bank[] {
  const byNormalizedShortName = new Map<string, Bank>();
  for (const b of banks) {
    byNormalizedShortName.set(normalize(b.shortName || ""), b);
  }

  const picked: Bank[] = [];
  const usedBins = new Set<string>();

  for (const wanted of POPULAR_SHORT_NAMES) {
    const key = normalize(wanted);
    let match = byNormalizedShortName.get(key);
    if (!match) {
      match = banks.find((b) => normalize(b.shortName || "").includes(key) || key.includes(normalize(b.shortName || "")));
    }
    if (match && !usedBins.has(match.bin)) {
      picked.push(match);
      usedBins.add(match.bin);
    }
  }

  // Nếu API trả ít ngân hàng phổ biến hơn danh sách trên, lấp cho đủ để lưới không trống.
  if (picked.length < 12) {
    for (const b of banks) {
      if (picked.length >= 12) break;
      if (!usedBins.has(b.bin)) {
        picked.push(b);
        usedBins.add(b.bin);
      }
    }
  }

  return picked;
}

type Props = {
  banks: Bank[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onSelect: (bank: Bank) => void;
};

export default function BankStep({ banks, loading, error, onRetry, onSelect }: Props) {
  const [query, setQuery] = useState("");

  const popular = useMemo(() => (banks ? pickPopularBanks(banks) : []), [banks]);

  const searchResults = useMemo(() => {
    if (!banks) return [];
    const q = normalize(query);
    if (!q) return [];
    return banks.filter(
      (b) =>
        normalize(b.name).includes(q) ||
        normalize(b.shortName).includes(q) ||
        normalize(b.code).includes(q),
    );
  }, [banks, query]);

  const isSearching = query.trim().length > 0;

  return (
    <section className="step" aria-labelledby="step1-title">
      <h1 id="step1-title" className="step-title">
        Bạn muốn nhận tiền vào đâu?
      </h1>

      <label className="search-box">
        <span className="search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          type="search"
          inputMode="search"
          placeholder="Tìm ngân hàng..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Tìm ngân hàng"
        />
      </label>

      {loading ? <StatusMessage tone="loading" text="Đang tải danh sách ngân hàng…" /> : null}
      {error ? (
        <StatusMessage tone="error" text={error} onRetry={onRetry} />
      ) : null}

      {!loading && !error && banks ? (
        isSearching ? (
          searchResults.length > 0 ? (
            <ul className="bank-list">
              {searchResults.map((b) => (
                <li key={b.bin}>
                  <button type="button" className="bank-list-item" onClick={() => onSelect(b)}>
                    <BankLogo bank={b} size={40} />
                    <span className="bank-list-name">
                      <strong>{b.shortName}</strong>
                      <span className="bank-list-fullname">{b.name}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="hint-text">Không tìm thấy ngân hàng nào khớp với "{query}".</p>
          )
        ) : (
          <div className="bank-grid" role="list">
            {popular.map((b) => (
              <button
                key={b.bin}
                type="button"
                className="bank-tile"
                role="listitem"
                onClick={() => onSelect(b)}
                aria-label={`Chọn ${b.shortName}`}
              >
                <BankLogo bank={b} size={56} />
                <span className="bank-tile-name">{b.shortName}</span>
              </button>
            ))}
          </div>
        )
      ) : null}
    </section>
  );
}
